import { Button, Dropdown, Input, ModalWrapper } from '@/shared';
import type { DropdownOption } from '@/shared/components/ui/Dropdown';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState, useEffect } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import * as yup from 'yup';
import { useFileUpload } from '@/shared/hooks/useFileUpload';
import { useUpdateResource } from '@/features/resources/hooks/useUpdateResource';
// import { useRemoveResourceFile } from '@/features/resources/hooks/useRemoveResourceFile';
import type { Resource } from '@/features/resources/api/resourceApi';

// -----------------------------
// Dropdown options
// -----------------------------
const categories: DropdownOption<string>[] = [
  { label: 'Template', value: 'TEMPLATE' },
  { label: 'Guide', value: 'GUIDE' },
  { label: 'Policy Document', value: 'POLICY_DOCUMENT' },
  { label: 'External Link', value: 'EXTERNAL_LINK' },
];

// -----------------------------
// Component
// -----------------------------
interface EditResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: Resource | null;
}

export const EditResourceModal: React.FC<EditResourceModalProps> = ({
  isOpen,
  onClose,
  resource,
}) => {
  const { upload, uploading, progress, error: uploadError } = useFileUpload();
  const { mutate: updateResource, isPending: isUpdating } = useUpdateResource();
  /* const { mutate: removeFile, isPending: isRemovingFile } =
    useRemoveResourceFile(); */

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isExistingFileRemoved, setIsExistingFileRemoved] = useState(false);
  // const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [uploadedResource, setUploadedResource] = useState<{
    path: string;
    url: string;
  } | null>(null);

  // Validation Schema
  const schema = yup.object({
    title: yup.string().required('Title is required'),
    details: yup.string().required('Details are required'),
    category: yup.string().required('Category is required'),
  });

  interface ResourceFormData {
    title: string;
    details: string;
    category: string;
  }

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ResourceFormData>({
    resolver: yupResolver(schema) as Resolver<ResourceFormData>,
  });

  // Pre-fill form when resource changes
  useEffect(() => {
    if (resource && isOpen) {
      reset({
        title: resource.title,
        details: resource.description || '',
        category: resource.category,
      });
      setSelectedFile(null);
      setUploadedResource(null);
      setIsExistingFileRemoved(false);
      // setIsConfirmingDelete(false);
    }
  }, [resource, isOpen, reset]);

  /* const handleRemoveClick = () => {
    setIsConfirmingDelete(true);
  };

  const confirmRemoveFile = () => {
    if (!resource) return;

    removeFile(resource.id, {
      onSuccess: () => {
        setIsExistingFileRemoved(true);
        setIsConfirmingDelete(false);
      },
    });
  };

  const cancelRemoveFile = () => {
    setIsConfirmingDelete(false);
  }; */

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setUploadedResource(null); // Reset upload status on new file selection
    }
  };

  const handleUploadClick = async () => {
    if (!selectedFile) return;

    const result = await upload(selectedFile, 'resources');
    if (result) {
      setUploadedResource(result);
    }
  };

  const onSubmit = async (data: ResourceFormData) => {
    if (!resource) return;

    // Prepare payload
    const payload: Partial<
      ResourceFormData & { fileUrl: string; fileType: string }
    > = {
      title: data.title,
      details: data.details,
      category: data.category,
    };

    // Only include fileUrl if a new file was uploaded
    if (uploadedResource) {
      payload.fileUrl = uploadedResource.path;
      payload.fileType = selectedFile?.type;
    }

    updateResource(
      { id: resource.id, data: payload },
      {
        onSuccess: () => {
          onClose();
          setSelectedFile(null);
          setUploadedResource(null);
        },
      },
    );
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-center min-h-screen p-5">
        <div className="bg-white rounded-4xl p-4 md:p-8 max-w-2xl w-full">
          <h3 className="text-xl md:text-3xl font-bold mb-2 text-text-primary">
            Edit resource
          </h3>
          <p className="mb-4 md:mb-8 text-base md:text-xl text-text-secondary">
            Update your property documents
          </p>

          {/* Form */}
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <Input
              label="Title"
              placeholder="Add title"
              {...register('title')}
              error={errors.title?.message}
              className="!text-base md:!text-xl"
            />

            <div className="flex flex-col gap-1">
              <label className="font-medium text-text-secondary">Details</label>
              <textarea
                {...register('details')}
                placeholder="Details about the resource"
                className="w-full rounded-md border border-gray-300 p-3 text-base md:text-xl h-[100px] focus:outline-none resize-none"
              />
              {errors.details?.message && (
                <p className="text-red-500 text-sm">{errors.details.message}</p>
              )}
            </div>

            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label="Category"
                  buttonClassName="!w-full"
                  options={categories}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.category && (
              <p className="text-red-500 text-sm">{errors.category.message}</p>
            )}

            {/* File Upload Section */}
            <div className="space-y-3">
              <label className="font-medium text-text-secondary">
                Document Upload
              </label>

              {/* Show existing file info if no new file selected */}
              {!selectedFile &&
              !uploadedResource &&
              resource?.fileUrl &&
              !isExistingFileRemoved ? (
                <div className="flex flex-col gap-2">
                  <div className="mb-2 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between">
                    <span className="text-sm text-gray-600 truncate max-w-[80%]">
                      Current file:{' '}
                      {resource.fileUrl.split('/').pop()?.split('?')[0] ||
                        'Existing Document'}
                    </span>
                    {/* {!isConfirmingDelete && (
                      <button
                        type="button"
                        onClick={handleRemoveClick}
                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                      >
                        Delete & Replace
                      </button>
                    )} */}
                  </div>

                  {/* {isConfirmingDelete && (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-lg animate-fadeIn">
                      <p className="text-sm text-red-800 font-medium mb-3">
                        Are you sure? This document will be permanently deleted
                        when you save your changes.
                      </p>
                      <div className="flex gap-3 justify-end">
                        <button
                          type="button"
                          onClick={cancelRemoveFile}
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1.5"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={confirmRemoveFile}
                          disabled={isRemovingFile}
                          className="text-sm bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-medium px-4 py-1.5 rounded-md transition-colors flex items-center gap-2"
                        >
                          {isRemovingFile ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                      </div>
                    </div>
                  )} */}
                </div>
              ) : (
                <>
                  {!selectedFile ? (
                    <div>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="edit-file-upload"
                      />
                      <label htmlFor="edit-file-upload">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors">
                          <div className="flex flex-col gap-4 items-center justify-center">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M12 13.5V3"
                                stroke="#141414"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M20.25 13.5V19.5H3.75V13.5"
                                stroke="#141414"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8.25 6.75L12 3L15.75 6.75"
                                stroke="#141414"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <p className="text-sm">
                              Click to replace file (PDF)
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                              Size limit: 10MB
                            </p>
                          </div>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="border border-gray-100 rounded-xl p-4 bg-[#F8F9FA]">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className="p-2 bg-white rounded-lg border border-gray-100">
                            <svg
                              className="w-5 h-5 text-[#5761EB]"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                          </div>
                          <div className="truncate font-medium text-sm text-text-primary">
                            {selectedFile.name}
                          </div>
                        </div>

                        {/* Clear selection button */}
                        {!uploading && !uploadedResource && (
                          <button
                            type="button"
                            onClick={() => setSelectedFile(null)}
                            className="text-gray-400 hover:text-[#5761EB] transition-colors p-1"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Upload Info/Progress */}
                      {uploading && (
                        <div className="space-y-1">
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-[#5761EB] h-full rounded-full transition-all duration-300 ease-out"
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-right text-text-secondary font-medium">
                            {Math.round(progress)}%
                          </p>
                        </div>
                      )}

                      {uploadedResource && (
                        <div className="flex items-center gap-2 text-[#24C661] text-sm font-medium bg-white p-2 rounded-lg border border-[#24C661]/20">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span>Upload Complete</span>
                        </div>
                      )}

                      {uploadError && (
                        <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-2 rounded-lg border border-red-100">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                          <span className="truncate">{uploadError}</span>
                        </div>
                      )}

                      {/* Separate Upload Button */}
                      {!uploadedResource && !uploading && (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full mt-3 border-[#5761EB]! text-[#5761EB]! hover:bg-[#5761EB]! hover:text-white! transition-all"
                          onClick={handleUploadClick}
                        >
                          Upload New Document
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="pt-0 md:pt-6">
              <Button
                variant="primary"
                size="lg"
                type="submit"
                className="w-full mb-0 md:mb-4 !font-bold text-base md:!text-xl"
                // Disable if uploading, or if a new file is selected but not uploaded yet
                disabled={
                  uploading ||
                  isUpdating ||
                  (!!selectedFile && !uploadedResource)
                }
                isLoading={isUpdating}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </ModalWrapper>
  );
};
